import React, { useMemo, useState } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import { authenticateNativeBiometric } from '../native/nativeSecureStorage';
import { nativeDoorAccessAvailable, nativeDoorAccessClientInfo, unlockNativeDoor } from '../native/nativeDoorAccess';
import './door-access.css';

const SHARED_REMS_EMAIL = '24rem@metrofire.ca.gov';

async function memberToken() {
  const token = await getMemberAccessToken();
  if (!token) throw new Error('Member sign in required.');
  return token;
}

function readableErrorMessage(value, fallback = '') {
  if (typeof value === 'string') {
    const text = value.trim();
    return text && text !== '[object Object]' ? text : fallback;
  }
  if (!value || typeof value !== 'object') return fallback;

  const direct = [value.message, value.error_description, value.detail, value.title]
    .find(candidate => typeof candidate === 'string' && candidate.trim());
  if (direct) return direct.trim();

  if (value.error && value.error !== value) {
    const nested = readableErrorMessage(value.error, '');
    if (nested) return nested;
  }
  return fallback;
}

async function doorRequest(token, body) {
  const response = await fetch('/api/door-access', {
    method: 'POST',
    cache: 'no-store',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const raw = await response.text().catch(() => '');
  let payload = {};
  if (raw) {
    try { payload = JSON.parse(raw); }
    catch (_) { payload = { message: raw }; }
  }

  if (!response.ok) {
    const fallback = response.status
      ? `Unable to complete Gear Room Access. HTTP ${response.status}.`
      : 'Unable to complete Gear Room Access.';
    const message = readableErrorMessage(payload?.error, '')
      || readableErrorMessage(payload?.message, '')
      || readableErrorMessage(payload, fallback);
    const error = new Error(message);
    error.code = readableErrorMessage(payload?.code, '')
      || readableErrorMessage(payload?.error?.code, '')
      || (response.status ? `HTTP_${response.status}` : '');
    error.status = response.status;
    throw error;
  }
  return payload;
}

async function requestCredential(token, clientInfo) {
  return doorRequest(token, {
    action: 'credential',
    appVersion: clientInfo?.version,
    appBuild: clientInfo?.build,
  });
}

async function recordAudit(token, payload, clientInfo = {}) {
  try {
    await doorRequest(token, {
      action: 'audit',
      ...payload,
      appVersion: clientInfo?.version,
      appBuild: clientInfo?.build,
    });
  } catch (_) {
    // The server already records credential issuance. A best-effort result update
    // must never trap a member next to the lockbox after the physical action finishes.
  }
}

function statusCopy(status) {
  switch (status) {
    case 'authenticating': return 'Confirming with Face ID…';
    case 'credential': return 'Preparing secure access…';
    case 'unlocking': return 'Connecting to the Gear Room lockbox…';
    case 'backup-authenticating': return 'Confirming backup access with Face ID…';
    case 'backup-sending': return 'Creating a one-time backup PIN and sending it to your verified mobile number…';
    default: return '';
  }
}

export function DoorAccessTile({ onOpen, member }) {
  const email = String(member?.email || '').trim().toLowerCase();
  const sharedRems = email === SHARED_REMS_EMAIL || member?.member_kind === 'rems_shared';
  if (sharedRems) return null;

  return (
    <button type="button" className="door-access-tile" onClick={onOpen}>
      <span className="door-access-tile-icon" aria-hidden="true">⌁</span>
      <span>
        <strong>Gear Room Access</strong>
        <small>Face ID + Bluetooth unlock</small>
      </span>
      <span className="door-access-tile-arrow" aria-hidden="true">→</span>
    </button>
  );
}

export default function DoorAccessView({ onBack, member }) {
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [requestId, setRequestId] = useState('');
  const [backupAvailable, setBackupAvailable] = useState(false);
  const [lastClientInfo, setLastClientInfo] = useState(null);
  const nativeAvailable = nativeDoorAccessAvailable();
  const email = String(member?.email || '').trim().toLowerCase();
  const sharedRems = email === SHARED_REMS_EMAIL || member?.member_kind === 'rems_shared';
  const busy = ['authenticating', 'credential', 'unlocking', 'backup-authenticating', 'backup-sending'].includes(status);

  const buttonLabel = useMemo(() => {
    if (!nativeAvailable) return 'iPhone App Required';
    if (status === 'success') return 'Unlock Again';
    if (busy) return 'Working…';
    return 'Unlock Gear Room';
  }, [busy, status, nativeAvailable]);

  const unlock = async () => {
    setMessage('');
    setRequestId('');
    setBackupAvailable(false);

    if (sharedRems) {
      setStatus('error');
      setMessage('The shared REMS account cannot unlock the Gear Room. Use an individual Line Medic or REMS Quick Access account.');
      return;
    }
    if (!nativeAvailable) {
      setStatus('error');
      setMessage('Gear Room Access is available in the Line Medic iPhone app.');
      return;
    }

    let token = '';
    let credential = null;
    let clientInfo = null;
    try {
      clientInfo = await nativeDoorAccessClientInfo();
      setLastClientInfo(clientInfo);

      setStatus('authenticating');
      const biometric = await authenticateNativeBiometric('Unlock the Line Medic Gear Room lockbox');
      if (!biometric?.success) throw new Error(readableErrorMessage(biometric?.message, 'Face ID was not completed.'));

      setStatus('credential');
      token = await memberToken();
      credential = await requestCredential(token, clientInfo);
      setRequestId(String(credential?.requestId || ''));

      setStatus('unlocking');
      await unlockNativeDoor({
        bluetoothName: credential?.bluetoothName,
        guestKey: credential?.guestKey,
      });

      await recordAudit(token, {
        requestId: credential?.requestId,
        eventType: 'unlock_result',
        method: 'bluetooth',
        result: 'success',
      }, clientInfo);

      setStatus('success');
      setMessage('✅ Gear Room Unlocked\nClose the lockbox firmly when finished. It will secure automatically.');
    } catch (error) {
      const bleAttemptFailed = Boolean(token && credential?.requestId);
      if (bleAttemptFailed) {
        await recordAudit(token, {
          requestId: credential.requestId,
          eventType: 'unlock_result',
          method: 'bluetooth',
          result: 'failure',
          errorCode: readableErrorMessage(error?.message, 'unlock_failed').slice(0, 120),
        }, clientInfo || lastClientInfo || {});
      }
      setBackupAvailable(bleAttemptFailed);
      setStatus('error');
      const failureMessage = readableErrorMessage(error?.message, '')
        || readableErrorMessage(error, 'Unable to unlock the Gear Room lockbox.');
      setMessage(`${failureMessage}${bleAttemptFailed ? ' Use the Backup PIN option below if Bluetooth will not complete the unlock.' : ''}`);
    }
  };

  const sendBackupPin = async () => {
    if (!backupAvailable || !requestId || busy) return;
    setMessage('');
    try {
      setStatus('backup-authenticating');
      const biometric = await authenticateNativeBiometric('Send a one-time Line Medic Gear Room backup PIN');
      if (!biometric?.success) throw new Error(readableErrorMessage(biometric?.message, 'Face ID was not completed.'));

      const clientInfo = lastClientInfo || await nativeDoorAccessClientInfo();
      setStatus('backup-sending');
      const token = await memberToken();
      await doorRequest(token, {
        action: 'backup_pin',
        requestId,
        appVersion: clientInfo?.version,
        appBuild: clientInfo?.build,
      });
      setBackupAvailable(false);
      setStatus('backup-sent');
      setMessage('A one-time Gear Room backup PIN was sent to your verified mobile number. Enter that PIN on the Line Medic Room lockbox keypad.');
    } catch (error) {
      setStatus('error');
      setMessage(readableErrorMessage(error?.message, '') || readableErrorMessage(error, 'Unable to send the backup PIN.'));
    }
  };

  return (
    <section className="tracking-card door-access-card">
      <div className="tracking-section-head">
        <div>
          <span className="my-gear-eyebrow">MY GEAR</span>
          <h2>Gear Room Access</h2>
          <p>Use your individual Line Medic or REMS Quick Access account and Face ID to unlock the Gear Room lockbox over Bluetooth.</p>
        </div>
        <button type="button" className="tracking-link-btn" onClick={onBack}>Back</button>
      </div>

      <div className="door-access-lockbox" aria-hidden="true">
        <div className="door-access-lockbox-ring">⌁</div>
        <strong>Line Medic Room</strong>
        <span>Bluetooth lockbox</span>
      </div>

      <div className="door-access-notes">
        <div><strong>Face ID is required every time.</strong><span>Your Line Medic sign-in alone does not unlock the box.</span></div>
        <div><strong>Move close to the lockbox.</strong><span>The normal unlock happens directly over Bluetooth.</span></div>
        <div><strong>No Lock button is needed.</strong><span>Close the lockbox firmly when finished; the SK4 secures itself automatically.</span></div>
        <div><strong>Bluetooth problem?</strong><span>After a failed Bluetooth attempt, you can re-confirm Face ID and send a one-time PIN to your verified mobile number.</span></div>
      </div>

      {busy && <div className="door-access-status working">{statusCopy(status)}</div>}
      {message && <div style={{ whiteSpace: 'pre-line' }} className={`door-access-status ${status === 'success' || status === 'backup-sent' ? 'success' : 'error'}`}>{message}</div>}
      {requestId && ['success', 'backup-sent'].includes(status) && <small className="door-access-request-id">Access record: {requestId.slice(0, 8)}</small>}

      {backupAvailable ? (
        <>
          <button type="button" className="tracking-btn tracking-btn-primary door-access-unlock" disabled={busy || sharedRems} onClick={sendBackupPin}>
            Use Backup PIN
          </button>
          <button type="button" className="tracking-btn door-access-unlock" disabled={busy || sharedRems} onClick={unlock}>
            Retry Bluetooth
          </button>
        </>
      ) : (
        <button type="button" className="tracking-btn tracking-btn-primary door-access-unlock" disabled={busy || sharedRems || !nativeAvailable} onClick={unlock}>
          {buttonLabel}
        </button>
      )}

      {!nativeAvailable && !sharedRems && (
        <small className="door-access-native-note">Physical unlock is available only in the native Line Medic iPhone app.</small>
      )}
    </section>
  );
}
